
/**
 * Match Analytics Engine
 * Centralized logic for calculating derived statistics from match history.
 * Used by: PressRoom, Scoreboard, TournamentAwards, StatsEngine
 */

export interface DetailedStats {
    aces: number;
    winners: number;
    errors: number;
    edges: number;
    spw: number; // Serve Points Won
    spt: number; // Serve Points Total
    rpw: number; // Return Points Won
    rpt: number; // Return Points Total
    gpc: number; // Game Points Converted
    gpo: number; // Game Points Opportunities
    totalPoints: number;
    comebackSize: number; // Max deficit overcome (if won)
}

export const INITIAL_DETAILED_STATS: DetailedStats = {
    aces: 0, winners: 0, errors: 0, edges: 0,
    spw: 0, spt: 0, rpw: 0, rpt: 0,
    gpc: 0, gpo: 0, totalPoints: 0, comebackSize: 0
};

/**
 * Calculates aggregated stats for a player across one or multiple matches.
 */
export const calculatePlayerStats = (matches: any[], playerId: string): DetailedStats => {
    // Clone initial to avoid mutation if we were reusing objects (simple primitives here, but good practice)
    const s: DetailedStats = { ...INITIAL_DETAILED_STATS };

    matches.forEach(m => {
        if (!m.history || m.history.length === 0) return;
        
        const maxScore = Math.max(m.s1, m.s2);
        // Heuristic for win limit based on played score
        const WIN_LIMIT = maxScore <= 11 && (m.s1 >= 11 || m.s2 >= 11) ? 11 : 21; 
        const GP_THRESHOLD = WIN_LIMIT - 1;

        let maxDeficit = 0;

        for(let i = 0; i < m.history.length - 1; i++) {
            const curr = m.history[i];
            const next = m.history[i+1];
            
            // Determine who won this specific point
            let pointWinnerId = null;
            if (next.score1 > curr.score1) pointWinnerId = m.p1;
            else if (next.score2 > curr.score2) pointWinnerId = m.p2;
            
            if (!pointWinnerId) continue;
            s.totalPoints++;

            const isMe = pointWinnerId === playerId;
            const isServer = curr.server === playerId;

            // Comeback Tracking
            // Calculate scores *before* this point result
            const p1Score = curr.score1;
            const p2Score = curr.score2;
            const myScore = m.p1 === playerId ? p1Score : p2Score;
            const oppScore = m.p1 === playerId ? p2Score : p1Score;
            
            if (oppScore > myScore) {
                const deficit = oppScore - myScore;
                if (deficit > maxDeficit) maxDeficit = deficit;
            }

            // Service Stats
            if (isServer) {
                s.spt++;
                if (isMe) s.spw++;
            } else {
                s.rpt++;
                if (isMe) s.rpw++; // Break point won
            }

            // Tags
            if (next.tag) {
                if (isMe) {
                    if (next.tag === 'Ace') s.aces++;
                    if (next.tag === 'Winner') s.winners++;
                    if (next.tag === 'Edge') s.edges++;
                }
                
                // If opponent made an error, it counts towards my stats in some contexts, 
                // but here we track specific "Errors" committed by the player.
                if (next.tag === 'Error' && !isMe) {
                    // Logic check: If !isMe and tag is Error, it means Opponent committed error.
                    // This function tracks stats FOR `playerId`. 
                    // So if `next.tag === 'Error'` and `isMe` is false (opponent won point), then `playerId` committed error.
                    // Wait, logic inversion: 
                    // If pointWinnerId != playerId (I lost point), and tag is Error, I made the error.
                    s.errors++;
                }
            }

            // Clutch Stats (Game Point)
            const p1AtGP = curr.score1 >= GP_THRESHOLD && curr.score1 > curr.score2;
            const p2AtGP = curr.score2 >= GP_THRESHOLD && curr.score2 > curr.score1;
            
            // Did I have a Game Point opportunity?
            if ((m.p1 === playerId && p1AtGP) || (m.p2 === playerId && p2AtGP)) {
                s.gpo++;
                if (isMe) s.gpc++;
            }
        }

        // Only attribute comeback size if the player actually won the match
        if (m.winner === playerId) {
            if (maxDeficit > s.comebackSize) s.comebackSize = maxDeficit;
        }
    });

    return s;
};
